import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { api } from '@/lib/api/client'

/**
 * Settings → Payment types: the tenant-managed layer above the fixed channel
 * list. A payment type is *where* collected money goes (HDFC Bank, Cash,
 * Payment Link); its channels are *how* it gets there (UPI, NEFT, cheque…).
 * The agent picks the type first, then one of its channels, and the type's
 * capture mode decides whether the manual capture form or the payment-link
 * flow runs.
 *
 * Wire contract: `/v1/admin/payment-types`, gated by PAYMENT_TYPE_VIEW
 * (reads) and PAYMENT_TYPE_MANAGE (writes). Errors come back as
 * `{code, message}` — see paymentTypeErrorMessage.
 */

/**
 * The doc's "Payment Mode" (Manual | Link). Named captureMode on the wire
 * because `paymentMode` already means the channel code everywhere else.
 */
export type CaptureMode = 'MANUAL' | 'LINK'

export interface PaymentType {
  paymentTypeId: string
  /** Stable key, derived from the name on creation and never edited. */
  code: string
  name: string
  description: string | null
  captureMode: CaptureMode
  /** Channel-type codes (UPI, NEFT, …) — at least one. */
  channels: string[]
  /** Transaction types it applies to — only REPAYMENT exists today. */
  applicableFor: string[]
  /** Display order in the app, ascending. */
  position: number
  /** Seeded on onboarding: editable and deactivatable, never deleted. */
  isSystem: boolean
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface ChannelType {
  code: string
  label: string
  /** The agent physically holds the instrument (cash, cheque, DD). */
  carried: boolean
  requiresReference: boolean
  /** What the reference is called for this channel — "Cheque number", "UTR / transaction reference". */
  referenceLabel: string | null
  digital: boolean
}

export interface CodeLabel {
  code: string
  label: string
}

/** Everything the form needs to offer choices, from one call. */
export interface PaymentTypeTemplate {
  channelTypes: ChannelType[]
  captureModes: CodeLabel[]
  applicableFor: CodeLabel[]
}

/**
 * Every key is required: the project runs with exactOptionalPropertyTypes
 * and a PUT replaces the row wholesale, so a field left out would not mean
 * "unchanged" but "cleared".
 */
export interface PaymentTypeUpdate {
  name: string
  description: string | null
  captureMode: CaptureMode
  channels: string[]
  applicableFor: string[]
  position: number
  active: boolean
}

/** The code is derived server-side from the name; this page never sends one. */
export type PaymentTypeCreate = PaymentTypeUpdate & { code?: string }

const LIST_KEY = ['settings', 'payment-types', 'list']
const TEMPLATE_KEY = ['settings', 'payment-types', 'template']

export function usePaymentTypes(includeInactive: boolean) {
  return useQuery({
    queryKey: [...LIST_KEY, includeInactive],
    queryFn: async () =>
      (await api.get<PaymentType[]>('/v1/admin/payment-types', { params: { includeInactive } })).data,
  })
}

export function usePaymentTypeTemplate() {
  return useQuery({
    queryKey: TEMPLATE_KEY,
    queryFn: async () =>
      (await api.get<PaymentTypeTemplate>('/v1/admin/payment-types/template')).data,
    // A fixed enum on the server: it changes only with a deploy.
    staleTime: Infinity,
  })
}

export function useCreatePaymentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: PaymentTypeCreate) =>
      (await api.post<PaymentType>('/v1/admin/payment-types', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  })
}

export function useUpdatePaymentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { paymentTypeId: string; body: PaymentTypeUpdate }) =>
      (await api.put<PaymentType>(`/v1/admin/payment-types/${params.paymentTypeId}`, params.body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  })
}

export function useDeletePaymentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (paymentTypeId: string) => {
      await api.delete(`/v1/admin/payment-types/${paymentTypeId}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  })
}

/**
 * The admin endpoints answer `{code, message}`. Switch on the code, not the
 * message: the message is written for a log line ("payment_type_in_use: 3
 * receipts, 1 payments") and the operator needs to be told what to do instead.
 */
export function paymentTypeErrorMessage(error: unknown, fallback: string): string {
  if (!isAxiosError(error)) return fallback
  if (error.response?.status === 403) return 'PAYMENT_TYPE_MANAGE is required.'
  const data = error.response?.data as { code?: string; message?: string } | undefined
  switch (data?.code) {
    case 'payment_type_name_taken':
      return 'A payment type with this name already exists.'
    case 'payment_type_code_taken':
      return 'A payment type with this code already exists.'
    case 'payment_type_in_use':
      return 'Collections have already been recorded against this payment type, so it cannot be deleted or moved between Manual and Payment link. Deactivate it instead.'
    case 'payment_type_is_system':
      return 'Built-in payment types cannot be deleted. Deactivate it instead.'
  }
  return data?.message ?? fallback
}
